import React, { useEffect, useState } from 'react';
import { SettingSystemUserHeader } from '../system-users/systemUserStyled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import { TaxSettlementContainer } from '../settlement-config/settlementConfigStyled';
import Button from '../../../components/button';
import TransferAgentSettingModal from './transferAgentSettingModal';
import { useBoolean, useGetTransferAgentSetting, useWealthlaneAwardApi } from '../../../hooks';
import { ModalStatusType } from '../../../types/common';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import { SkeletonCircle, SkeletonLine } from '../../../components/skeleton';
import { ProfileContainer, CopyContainer } from '../company-profile/profile.styled';
import {
  TransferAgentIntegrationService,
  Wealthlane_AwardService_Dtos_TransferAgents_TransferAgentIntegrationDto,
} from '../../../services/wealthlane-award-services';
import CustomCheckBox from '../../../components/radio-button/customCheckBox';
import { RadioButtonWrapper } from '../../../components/radio-button/radioButton.styled';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';

const TransferAgentSetting = () => {
  const queryClient = useQueryClient();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const { handleRequest } = useWealthlaneAwardApi();
  const { data: transferAgentSetting, isLoading: isTransferAgentSettingLoading } =
    useGetTransferAgentSetting(handleRequest);
  const [transferAgentDetails, setTransferAgentDetails] =
    useState<Wealthlane_AwardService_Dtos_TransferAgents_TransferAgentIntegrationDto>({});

  const [editStatus, setEditStatus] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(transferAgentSetting?.isEnabled);
  }, [transferAgentSetting]);

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(
        <ToastComponent label='Success' message={`${fieldName} copied to clipboard.`} />
      );
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(
        <ToastComponent label='Success' message={`${fieldName} copied to clipboard.`} />
      );
    }
  };

  const handleSave = () => {
    setLoading(true);
    handleRequest(
      TransferAgentIntegrationService.postApiAwardApiAppTransferAgentSetTAIntegrationStatus(
        transferAgentSetting?.id,
        isActive
      )
    )
      .then((res) => {
        if (res.isSuccess) {
          void queryClient.invalidateQueries('getTransferAgentSetting');
          toast.success(<ToastComponent label='Success' message='Transfer Agent status edited.' />);
          setEditStatus(false);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log('err', err);
        setLoading(false);
      })
      .finally(() => {
        setEditStatus(false);
        setLoading(false);
      });
  };

  const TRANSFER_AGENT_DETAILS = [
    {
      title: 'Transfer Agent Name',
      icon: 'apartment',
      isContent: false,
      content: transferAgentSetting?.transferAgentName
        ? transferAgentSetting?.transferAgentName
        : '-',
      canCopy: false,
    },
    {
      title: 'Client ID',
      icon: 'pin',
      isContent: false,
      content: transferAgentSetting?.clientId ? transferAgentSetting?.clientId : '-',
      canCopy: true,
    },
    {
      title: 'Client Secret',
      icon: 'pin',
      isContent: false,
      content: transferAgentSetting?.clientSecret ? transferAgentSetting?.clientSecret : '-',
      canCopy: true,
    },
    {
      title: 'Identifier',
      icon: 'pin',
      isContent: false,
      content: transferAgentSetting?.identifier ? transferAgentSetting?.identifier : '-',
      canCopy: true,
    },
    {
      title: 'Secret Key',
      icon: 'pin',
      isContent: false,
      content: transferAgentSetting?.secretKey ? transferAgentSetting?.secretKey : '-',
      canCopy: true,
    },
    {
      title: 'Status',
      icon: 'pin',
      isContent: false,
      content: (
        <div className='flex justify-between items-center gap-2 w-full'>
          {editStatus ? (
            <RadioButtonWrapper>
              <input
                type='checkbox'
                name={'isActive'}
                checked={isActive}
                onChange={(event: any) => {
                  // handleCheckAll(event.target.checked);
                  setIsActive(event.target.checked);
                }}
              />
              <label htmlFor='isActive'>
                <Heading variant='body-s' title={'Is Active'} />
              </label>
            </RadioButtonWrapper>
          ) : (
            <p>{transferAgentSetting?.isEnabled ? 'Active' : 'Deactivate'}</p>
          )}
          {editStatus ? (
            <div className='flex gap-2'>
              <Button
                title={'Cancel'}
                variant='secondary-red-outline'
                onClick={() => {
                  setEditStatus(false);
                }}
              />
              <Button
                title={'Save'}
                variant='secondary-outline'
                onClick={() => {
                  handleSave();
                }}
                loading={loading}
              />
            </div>
          ) : (
            <Button
              title={'Edit'}
              variant='secondary-outline'
              onClick={() => {
                setEditStatus(true);
              }}
            />
          )}
        </div>
      ),
      canCopy: false,
    },
    {
      title: 'Description',
      icon: 'pin',
      isContent: false,
      content: transferAgentSetting?.description ? transferAgentSetting?.description : '-',
      canCopy: false,
    },
  ];
  return (
    <>
      <SettingSystemUserHeader>
        <Heading variant='h3' title='Transfer Agent Setting' />
        <Heading variant='body-s' title='Integrate with Transfer Agent' color={COLORS.Gray} />
      </SettingSystemUserHeader>
      <TaxSettlementContainer className='pb-8'>
        <>
          <div className='flex justify-between items-center'>
            <Heading variant='h4' title='Transfer Agent Setting' className='mb-2' />
            {transferAgentSetting?.transferAgentId ? (
              <Button
                title='Edit Transfer Agent'
                variant='primary-outline'
                onClick={() => {
                  openModal();
                  setModalStatus('Edit');
                  setTransferAgentDetails(transferAgentSetting);
                }}
                //   loading={loading}
                //   disabled={data == null}
              />
            ) : (
              <Button
                title='Add Transfer Agent'
                variant='primary'
                onClick={() => {
                  openModal();
                }}
                //   loading={loading}
                //   disabled={data == null}
              />
            )}
          </div>
        </>
      </TaxSettlementContainer>
      {transferAgentSetting?.transferAgentId && (
        <ProfileContainer>
          {isTransferAgentSettingLoading && (
            <>
              {TRANSFER_AGENT_DETAILS.map((item) => (
                <div className='list list-loading'>
                  <div className='list-icon'>
                    <SkeletonCircle size='table' shape='circle' />
                  </div>
                  <div className='list-content grid grid-cols-12'>
                    <SkeletonLine size='table' containerClassName='col-span-3' />
                    <SkeletonLine size='table' containerClassName='col-span-9' />
                  </div>
                </div>
              ))}
            </>
          )}
          {!isTransferAgentSettingLoading &&
            TRANSFER_AGENT_DETAILS.map((item) => (
              <div className='list grid sm:grid-cols-4 lg:grid-cols-12'>
                <div className='list-icon sm:col-span-2 lg:col-span-4'>
                  <span className='material-symbols-outlined'>{item.icon}</span>
                  <Heading title={item.title} variant='body-s' />
                </div>
                <div className='list-content sm:col-span-2 lg:col-span-8'>
                  {item.isContent ? (
                    <>{item.content}</>
                  ) : (
                    <div className='flex items-center gap-4'>
                      <Heading title={item.content} variant='body-s' className='flex-1' />
                      {item.canCopy && item.content !== '-' && typeof item.content === 'string' && (
                        <CopyContainer>
                          <span
                            className='material-symbols-outlined copy-icon'
                            onClick={() =>
                              handleCopyToClipboard(item.content as string, item.title)
                            }>
                            content_copy
                          </span>
                          <div className='copy-tooltip'>Copy</div>
                        </CopyContainer>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
        </ProfileContainer>
      )}

      <AnimationWrapper>
        {isModalOpen && (
          <TransferAgentSettingModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            setModalStatus={setModalStatus}
            loading={false}
            modalStatus={modalStatus}
            transferAgentDetails={transferAgentDetails}
          />
        )}
      </AnimationWrapper>
    </>
  );
};

export default TransferAgentSetting;
